import { Quote, QuoteRequest, QuoteStatus, QuoteStub, QuoteStubFromAttributes, QuotingEntity } from './quoting.model';

export class QuotingHelper {
  static isStub(entity: QuotingEntity): entity is QuoteStub {
    return !entity?.completenessLevel || entity.completenessLevel === 'Stub';
  }

  static isNotStub(entity: QuotingEntity): entity is Quote | QuoteRequest {
    return !QuotingHelper.isStub(entity);
  }

  /**
   * sorts entity stubs to the bottom and all other quotes and quote requests descending by creationDate
   */
  static sort(a: QuotingEntity, b: QuotingEntity): number {
    if (QuotingHelper.isStub(a) !== QuotingHelper.isStub(b)) {
      return QuotingHelper.isStub(a) ? 1 : -1;
    } else if (QuotingHelper.isNotStub(a) && QuotingHelper.isNotStub(b)) {
      return b.creationDate - a.creationDate;
    }
    return 0;
  }

  static state(entity: QuotingEntity): QuoteStatus {
    if (QuotingHelper.isNotStub(entity)) {
      if (QuotingHelper.isQuote(entity)) {
        // CUSTOMIZATION: Presidio inbound — 4 statuses (Submitted / Processing / Rejected / Expired).
        // Submitted = needs Approve/Reject (integration). Processing/Rejected set by MyPresidio actions.
        // Expired = past validToDate when not Processing or Rejected.
        if (entity.rejected) {
          return 'Rejected';
        }
        if (entity.purchased) {
          return 'Processing';
        }
        if (entity.validToDate < new Date().getTime()) {
          return 'Expired';
        }
        return 'Submitted';
      } else {
        // QuoteRequest create/submit path (DIAS add-to-quote) — keep New vs Submitted.
        return entity.submittedDate ? 'Submitted' : 'New';
      }
    }
    // List stubs / incomplete rows
    const stub = entity as QuoteStubFromAttributes;
    if (stub.rejected) {
      return 'Rejected';
    }
    if (stub.purchased) {
      return 'Processing';
    }
    if (typeof stub.validToDate === 'number' && stub.validToDate < new Date().getTime()) {
      return 'Expired';
    }
    // Inbound Quote stubs default to Submitted (needs Approve/Reject).
    if (stub.type === 'Quote' || stub.number) {
      return 'Submitted';
    }
    if (stub.type === 'QuoteRequest') {
      return stub.submittedDate ? 'Submitted' : 'New';
    }
    return 'Unknown';
  }

  static itemCount(entity: { items?: unknown[]; itemCount?: number }): number {
    return entity.items?.length ?? entity?.itemCount;
  }

  static asQuote(entity: QuotingEntity): Quote {
    return entity as Quote;
  }

  static asQuoteRequest(entity: QuotingEntity): QuoteRequest {
    return entity as QuoteRequest;
  }

  private static isQuote(entity: QuotingEntity): entity is Quote {
    return QuotingHelper.isNotStub(entity) && entity.type === 'Quote';
  }
}
